import { parseMentions } from '~/utils/mention-helpers';

/** Inline purple mention styling — identical to the old MentionOverlay token. */
export const MENTION_SPAN_CLASS = 'text-purple-600 font-medium';

function isBlock(el: HTMLElement): boolean {
  return el.tagName === 'DIV' || el.tagName === 'P';
}

function walk(
  root: HTMLElement,
  onText: (s: string) => void,
  mention: (el: HTMLElement) => string
): string {
  let out = '';
  const visit = (node: Node) => {
    node.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE) {
        out += (child as Text).data;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        if (el.tagName === 'BR') {
          out += '\n';
        } else if (el.dataset && el.dataset.mentionId) {
          out += mention(el);
        } else if (isBlock(el)) {
          if (out.length > 0 && !out.endsWith('\n')) out += '\n';
          visit(el);
        } else {
          visit(el);
        }
      }
    });
  };
  visit(root);
  onText(out);
  return out.replace(/\n$/, '');
}

/** DOM → wire string with `<@id|name>` mention tokens. */
export function serializeEditable(root: HTMLElement): string {
  return walk(
    root,
    () => {},
    el => `<@${el.dataset.mentionId}|${(el.textContent ?? '').replace(/^@/, '')}>`
  );
}

/** DOM → display text (`@Name` for mentions); the value passed to onChange. */
export function readDisplayText(root: HTMLElement): string {
  return walk(
    root,
    () => {},
    el => el.textContent ?? ''
  );
}

/**
 * Given the text BEFORE the caret within a single text node and the caret
 * offset, return the active `@query` (and its `@` index) or null. Mirrors the
 * old findMentionTrigger rule: `@` must start the string or follow whitespace,
 * and no whitespace may sit between `@` and the caret.
 */
export function findActiveMentionQuery(
  textBeforeCaret: string,
  caretOffset: number
): { query: string; at: number } | null {
  const slice = textBeforeCaret.slice(0, caretOffset);
  const m = /(?:^|\s)@([^\s@]*)$/.exec(slice);
  if (!m) return null;
  const at = m.index === 0 && slice[0] === '@' ? 0 : slice.lastIndexOf('@');
  return { query: m[1], at };
}

/** Wire string → DOM nodes for seeding the editable. */
export function buildEditableChildren(serialized: string, doc: Document): Node[] {
  const nodes: Node[] = [];
  for (const frag of parseMentions(serialized)) {
    if (frag.type === 'mention') {
      const span = doc.createElement('span');
      span.dataset.mentionId = frag.userId;
      span.setAttribute('contenteditable', 'false');
      span.className = MENTION_SPAN_CLASS;
      span.textContent = `@${frag.displayName}`;
      nodes.push(span);
    } else {
      const parts = frag.content.split('\n');
      parts.forEach((part, i) => {
        if (i > 0) nodes.push(doc.createElement('br'));
        if (part) nodes.push(doc.createTextNode(part));
      });
    }
  }
  return nodes;
}
